"use client";

import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Button,
  Alert,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormControl,
} from "@mui/material";
import axios from "axios";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { fetchAccountsName } from "../customerManagement/customerManagementThunks";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import TransactionOverview from "./TransactionOverview";
import sampleEcxelDeviceType from "./sample-update-deviceType.xlsx";
import sampleExcelIMEIUpdate from "./sample-update-imei.xlsx";
import sampleExcelSIMUpdate from "./sample-update-sim-status.xlsx";
import sampleDeviceDetails from "./sample-update-device-details.xlsx";
import sampleBulkVerification from "./sample-bulk-kyc-verification.xlsx"
import api from "../../components/commonComponents/axiosSetup";
import { accountName } from "../customerManagement/utility/authUtils";
import { BASE_URL } from "../../config";

const actionTypeOptions = {
  "Update SIM Status": [
    { value: "Activate SIM", label: "Activate SIM" },
    { value: "Safe Custudy", label: "Safe Custudy" },
  ],
  "State Change": [
    { value: "stateUpdate", label: "State Update" },
    { value: "stateRollback", label: "State Rollback" },
  ],
  Others: [{ value: "misc", label: "Miscellaneous" }],
};

const category = [
  { value: "Update Device Details", label: "Update Device Details" },
  // { value: "Update IMEI", label: "Update IMEI" },
  // { value: "Update Device Type", label: "Update Device Type" },
  // { value: "Update SIM Status", label: "Update SIM Status" },
  { value: "Bulk KYC Verification", label: "Bulk KYC Verification" },
];

// const plans = [
//   { planId: "plan1", planName: "Basic Plan" },
//   { planId: "plan2", planName: "Premium Plan" },
//   { planId: "plan3", planName: "Enterprise Plan" },
// ];

const sampleFiles = {
  "Update IMEI": sampleExcelIMEIUpdate,
  "Update Device Type": sampleEcxelDeviceType,
  "Update SIM Status": sampleExcelSIMUpdate,
  "Update Device Details": sampleDeviceDetails,
  "Bulk KYC Verification" :sampleBulkVerification,
};

const instructionsConfig = {
  "Update Device Details": {
    title: "Instructions for Update Device Details",
    items: [
      "Template format: (msisdn, imei, device type, device model) e.g. ('917849037653197, 317000007653197, GPS, AIS 140')",
      "Ensure the column headers are exactly as shown above or download the template provided below",
      "Each MSISDN and IMEI must be unique",
      "MSISDN must be 12–15 digits long, without any prefix (such as 0, 91, or +91)",
      "IMEI must be 14–16 digits long",
      "Device Type and Device Model fields are mandatory",
      "A maximum of 1,000 records can be uploaded at once",
      "Recommendation: Download the template using the button below, fill in or edit your data, and re-upload.",
    ],
    requiredHeaders: ["msisdn", "imei", "device type", "device model"],
  },
  "Update IMEI": {
    title: "Instructions for IMEI Update",
    items: [
      "Template example (msisdn,imei) e.g('917849037653197, 317000007653197)",
      "Ensure the headers are exactly as shown in the example above or download the template below",
      "All MSISDN and IMEI entries should be unique",
      "MSISDN should be 12-15 digits without any prefix (like 0, 91, or +91)",
      "IMEI should be 14-16 digits",
      "Maximum 1,000 records can be uploaded at once",
      "Suggestion: Download the template by clicking on the button below, make required alterations and upload.",
    ],
    requiredHeaders: ["msisdn", "imei"],
  },
  "Update Device Type": {
    title: "Instructions for Device Type Update",
    items: [
      "Template example (msisdn,device_type) e.g('917849037653197, device_name) ",
      "Ensure the headers are exactly as shown in the example above or download the template below",
      "MSISDN should be 12-15 digits without any prefix (like 0, 91, or +91)",
      "Device type should be selected from predefined options",
      "Maximum 1,000 records can be uploaded at once",
      "Suggestion: Download the template by clicking on the button below, make required alterations and upload.",
    ],
    requiredHeaders: ["msisdn", "device_type"],
  },
  "Update SIM Status": {
    title: "Instructions for SIM Status Update",
    items: [
      "Template example msisdn e.g '917849037653197",
      "Ensure the headers are exactly as shown in the example above or download the template below",
      "MSISDN should be 12-15 digits without any prefix (like 0, 91, or +91)",
      "Maximum 1,000 records can be uploaded at once",
      "Suggestion: Download the template by clicking on the button below, make required alterations and upload.",
    ],
    requiredHeaders: ["msisdn"],
  },
  "Bulk KYC Verification": {
    title: "Instructions for Bulk KYC Verification",
    items: [
    "Excel file must have 3 sheets - data will be read from the 3rd sheet",
    "Data starts from row 5 (after headers and instruction rows)",
    "All fields are mandatory: SR_NO, ICCID (19-20 digits), Device IMEI (15 digits), Device SR.NO, Device Make, Device Model, Engine No, Chassis No, VIN/Reg Number, Name, RMN, Address, Proof of Address, Document, Document No",
    "ICCID must be exactly 19-20 digits long",
    "Device IMEI must be exactly 15 digits long",
    "Maximum 1,000 records can be uploaded at once",
    "Download the template below and fill in all mandatory fields starting from row 5",
  ],
    requiredHeaders: [],
  },
};
const processKYCData = (worksheet) => {
  const processedData = [];
  
  // Column mapping based on your Excel structure
  const columnMapping = {
    A: "sr_no",          // SR_NO
    B: "iccid",          // ICCID
    C: "device_imei",    // DEVICE_IMEI
    D: "device_srno",    // DEVICE_SR.NO
    E: "device_make",    // DEVICE_MAKE
    F: "device_model",  
    G:"device_type",
    H: "device_type_other" ,
    I: "engine_no",      // ENGINE_NO
    J: "chassis_no",     // CHASSIS_NO
    K: "vin_reg_number", // VIN/REG_NUMBER
    L: "name",           // NAME
    M: "rmn",            // RMN (Registered Mobile No.)
    N: "address",        // Address
    O: "proof_of_address_document", // PROOF OF ADDRESS       // DOCUMENT
    P: "proof_of_address_document_no" ,    // DOC NO.
    Q: "proof_of_identity_document", // PROOF OF ADDRESS       // DOCUMENT
    R: "proof_of_identity_document_no" ,
  };
  
  // Get the range of the worksheet
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  console.log("Worksheet range:", range);
  
  // Start from row 5 (index 4 in 0-based) - this is where actual data starts
  for (let rowIndex = 4; rowIndex <= range.e.r; rowIndex++) {
    const rowObj = {};
    let hasData = false;
    
    // Read all columns A to O
    Object.entries(columnMapping).forEach(([excelCol, fieldName]) => {
      const cellAddress = `${excelCol}${rowIndex + 1}`; // Excel uses 1-based row numbering
      const cell = worksheet[cellAddress];
      
      let cellValue = "";
      if (cell) {
        // Handle different cell types
        if (cell.t === 'n') {
          // Number type - convert to string without scientific notation
          cellValue = cell.v.toString();
        } else if (cell.t === 's') {
          // String type
          cellValue = cell.v.toString().trim();
        } else {
          cellValue = (cell.v || "").toString().trim();
        }
      }
      
      if (cellValue !== "") {
        hasData = true;
      }
      
      rowObj[fieldName] = cellValue;
    });
    
    // Stop if row is completely empty
    if (!hasData) {
      console.log(`Stopping at row ${rowIndex + 1} - no data found`);
      break;
    }
    
    // Skip rows with instruction text or header text
    const iccidValue = rowObj.iccid || "";
    if (
      iccidValue.toLowerCase().includes("secure authentication") ||
      iccidValue.toLowerCase().includes("data cell must") ||
      iccidValue.toLowerCase().includes("do not manipulate") ||
      iccidValue.toLowerCase().includes("do not border") ||
      iccidValue.toLowerCase().includes("convert to cell") ||
      iccidValue.toLowerCase().includes("do not convert") ||
      iccidValue.toLowerCase().includes("poa & poi") ||
      iccidValue.toLowerCase().includes("the date of sr") ||
      iccidValue === "ICCID*" // Skip header row
    ) {
      console.log(`Skipping instruction/header row ${rowIndex + 1}:`, iccidValue);
      continue;
    }
    
    // Add validation
    let status = "success";
    let errorMessage = "";
    
    if (!rowObj.iccid || rowObj.iccid === "") {
      status = "error";
      errorMessage = "ICCID is required";
    } else if (!rowObj.device_imei || rowObj.device_imei === "") {
      status = "error";
      errorMessage = "Device IMEI is required";
    } else if (!rowObj.name || rowObj.name === "") {
      status = "error";
      errorMessage = "Name is required";
    } else if (rowObj.iccid && rowObj.iccid.length < 19) {
      status = "error";
      errorMessage = `Invalid ICCID format (too short: ${rowObj.iccid.length} digits)`;
    } else if (rowObj.device_imei && rowObj.device_imei.length !== 15) {
      status = "error";
      errorMessage = `Invalid Device IMEI format (should be 15 digits, found: ${rowObj.device_imei.length})`;
    }
    
    rowObj.status = status;
    rowObj.errorMessage = errorMessage;
    
    console.log(`Row ${rowIndex + 1} processed:`, rowObj);
    processedData.push(rowObj);
  }
  
  console.log("Final processed KYC data:", processedData);
  return processedData;
};
function TransactionExcelUpload({
  formData = {},
  onFormDataChange = () => {},
  onNext = () => {},
}) {
  const [errors, setErrors] = useState({});
  const [instructionsExpanded, setInstructionsExpanded] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [localFormData, setLocalFormData] = useState(formData || {});
  const [activeStep, setActiveStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState(null);
  const steps = ["Bulk Whitelisting", "Overview"];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  // Update local state whenever formData prop changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setLocalFormData(formData);
      // Set selected items from formData if they exist
      if (formData.category) {
        setSelectedCategory(
          category.find((item) => item.value === formData.category) || null
        );
      }
      if (formData.actionType && formData.category) {
        setSelectedActionType(
          actionTypeOptions[formData.category]?.find(
            (item) => item.value === formData.actionType
          ) || null
        );
      }
    }
  }, [formData]);

  const getPlansName = async () => {
    const accName = accountName();
    try {
      const response = await api.get(
        `${BASE_URL}apis/get/plans?accountName=${accName}`
      );
      setPlans(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getPlansName();
  }, []);

  // Debug effect to console log when category changes
  useEffect(() => {
    console.log("Category changed:", localFormData?.category);
    console.log("Selected category object:", selectedCategory);
    if (selectedCategory) {
      console.log(
        "Instructions for selected category:",
        instructionsConfig[selectedCategory.value]
      );
    }
  }, [localFormData?.category, selectedCategory]);

  const handleInputChange = (field, value) => {
    // Update local state with complete merged object
    const updatedFormData = { ...localFormData, [field]: value };
    setLocalFormData(updatedFormData);

    // Debug log for plan-related fields
    if (field === "planType" || field === "planId") {
      console.log(`Setting ${field} to:`, value);
      console.log("Updated form data:", updatedFormData);
    }

    // Call prop function if it exists - pass complete object
    if (typeof onFormDataChange === "function") {
      onFormDataChange(updatedFormData);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!localFormData.accountName) {
      newErrors.accountName = "Account Name is required";
    }

    if (!selectedCategory) {
      newErrors.category = "Category is required";
    }
    if (localFormData.actionType === "Activate SIM" && !selectedPlan) {
      newErrors.planType = "Plan Type is required";
    }

    // Action Type is not mandatory anymore
    // if (!localFormData.actionType) {
    //   newErrors.actionType = "Action Type is required";
    // }

    if (!localFormData.uploadedFile) {
      newErrors.file = "Please upload a file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateExcelHeaders = (headers, actionType) => {
    const requiredHeaders =
      instructionsConfig[actionType]?.requiredHeaders || [];
    const missingHeaders = requiredHeaders.filter(
      (header) => !headers.includes(header)
    );

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(", ")}`);
    }

    return true;
  };

const handleFileUpload = async (file) => {
  try {
    if (!selectedCategory) {
      setErrors((prev) => ({
        ...prev,
        file: "Please select Category first",
      }));
      return;
    }

    if (
      selectedCategory.value === "Update SIM Status" &&
      !localFormData.actionType
    ) {
      setErrors((prev) => ({
        ...prev,
        file: "Please select Action Type first",
      }));
      return;
    }

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error("Only Excel files (.xlsx, .xls) are supported");
    }

    if (file.size > 50 * 1024 * 1024) {
      throw new Error("File size should not exceed 50 MB");
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        
        // Log all sheet names for debugging
        console.log("Available sheets:", workbook.SheetNames);
        
        // For Bulk KYC Verification, use the 3rd sheet (index 2)
        let sheetName;
        let worksheet;
        
        if (selectedCategory?.value === "Bulk KYC Verification") {
          // Get the 3rd sheet (index 2)
          if (workbook.SheetNames.length >= 3) {
            sheetName = workbook.SheetNames[2]; // 3rd sheet
          } else {
            throw new Error("Excel file must have at least 3 sheets for KYC Verification");
          }
          worksheet = workbook.Sheets[sheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          throw new Error("File is empty");

        }

        debugger;
         const kycHeadersRow = jsonData[2];
  const requiredHeaders = [
    "SR_NO", "ICCID*", "DEVICE_IMEI*", "DEVICE_SR.NO*", "DEVICE_MAKE*", "DEVICE_MODEL*",
    "DEVICE_TYPE*", "DEVICE_TYPE_OTHERS*", "ENGINE_NO*", "CHASSIS_NO*", "VIN/REG_NUMBER*",
    "NAME*", "Registered Mobile no. (RMN)*", "Address*", "PROOF OF ADDRESS*",
    "PROOF OF IDENTITY*", 
  ];
  const missingHeaders = requiredHeaders.filter(
    (header) => !kycHeadersRow?.includes(header)
  );
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers in KYC sheet: ${missingHeaders.join(", ")}`);
  }
        const nonEmptyRows = jsonData
          .slice(1)
          .filter((row) =>
            row.some(
              (cell) => cell !== undefined && cell !== null && cell !== ""
            )
          );

        if (nonEmptyRows.length > 1000) {
          throw new Error("Maximum 1000 records allowed");
        }
          
          console.log("Processing Bulk KYC Verification from sheet:", sheetName);
          
          const processedData = processKYCData(worksheet);
          
          console.log("KYC Processed Data:", processedData);
          
          if (processedData.length === 0) {
            throw new Error("No data found in the Excel file. Please ensure data starts from row 5.");
          }

          const updatedFormData = {
            ...localFormData,
            uploadedFile: file,
            fileData: processedData,
            processedData: processedData,
          };

          setLocalFormData(updatedFormData);
          setFileName(file.name);

          if (typeof onFormDataChange === "function") {
            onFormDataChange(updatedFormData);
          }

          setErrors((prev) => ({ ...prev, file: "" }));
          return;
        }

        // For other categories, use the first sheet
        sheetName = workbook.SheetNames[0];
        worksheet = workbook.Sheets[sheetName];

        // For non-KYC files, continue with existing logic
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          throw new Error("File is empty");
        }

        const headers = jsonData[0].map((header) =>
          header.toString().toLowerCase()
        );

        validateExcelHeaders(headers, selectedCategory.value);

        const nonEmptyRows = jsonData
          .slice(1)
          .filter((row) =>
            row.some(
              (cell) => cell !== undefined && cell !== null && cell !== ""
            )
          );

        if (nonEmptyRows.length > 1000) {
          throw new Error("Maximum 1000 records allowed");
        }

        const processedData = nonEmptyRows.map((row) => {
          const rowObj = {};
          headers.forEach((header, index) => {
            rowObj[header] = row[index]?.toString() || "";
          });

          const msisdnValue = rowObj.msisdn;
          let status = "success";
          let errorMessage = "";

          if (!msisdnValue) {
            status = "error";
            errorMessage = "MSISDN is required";
          } else if (!/^\d+$/.test(msisdnValue)) {
            status = "error";
            errorMessage = "MSISDN should contain only digits";
          } else if (msisdnValue.length < 12 || msisdnValue.length > 15) {
            status = "error";
            errorMessage = `MSISDN length should be between 12 and 15 digits (found: ${msisdnValue.length})`;
          }

          rowObj.status = status;
          rowObj.errorMessage = errorMessage;

          return rowObj;
        });

        const updatedFormData = {
          ...localFormData,
          uploadedFile: file,
          fileData: jsonData,
          processedData: processedData,
        };

        setLocalFormData(updatedFormData);
        setFileName(file.name);

        if (typeof onFormDataChange === "function") {
          onFormDataChange(updatedFormData);
        }

        setErrors((prev) => ({ ...prev, file: "" }));
      } catch (error) {
        console.error("Error processing file:", error);
        setErrors((prev) => ({ ...prev, file: error.message }));
      }
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      setErrors((prev) => ({ ...prev, file: "Failed to read file" }));
    };

    reader.readAsArrayBuffer(file);
  } catch (error) {
    console.error("File upload error:", error);
    setErrors((prev) => ({ ...prev, file: error.message }));
  }
};

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleNext = () => {
    if (validateForm()) {
      setActiveStep(1);
      if (typeof onNext === "function") {
        onNext();
      }
    }
  };

  const handlePrevious = () => {
    setActiveStep(0);
  };

  const handleSubmitClick = () => {
    // No longer needed here, as it's handled in OverviewPage
  };

  const handleSubmit = async () => {
    // No longer needed here, as it's handled in OverviewPage
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    // Reset the form and go back to step 0
    setLocalFormData({});
    setActiveStep(0);
    navigate("/bulktransaction");
  };

  const [currentInstructions, setCurrentInstructions] = useState(null);

  // Update instructions when category changes
  useEffect(() => {
    console.log("Setting instructions for category:", localFormData?.category);
    if (selectedCategory && selectedCategory.value) {
      const instructions = instructionsConfig[selectedCategory.value];
      console.log("Found instructions:", instructions);
      setCurrentInstructions(instructions);
    } else {
      setCurrentInstructions(null);
    }
  }, [selectedCategory]);

  const dispatch = useDispatch();
  const { accountNames, loading } = useSelector(
    (state) => state.customerManagement.accounts
  );
  useEffect(() => {
    dispatch(fetchAccountsName());
  }, [dispatch]);

  // Auto-select account name if there's only one option
  useEffect(() => {
    if (
      accountNames &&
      accountNames.length === 1 &&
      !localFormData.accountName
    ) {
      handleInputChange("accountName", accountNames[0].accountName);
    }
  }, [accountNames]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          marginTop: 12,
          px: 7, // shorthand for padding-left + padding-right
        }}
      >
        {/* Stepper */}
        <Box
          sx={{
            width: "100%",
            mb: 4,
            display: "flex",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--bg)",
              color: "white",
              padding: "10px 20px",
              borderRadius: "4px 0 0 4px",
              position: "relative",
              zIndex: 2,
            }}
          >
            <Typography
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: activeStep === 0 ? "white" : "#A0AEC0",
                color: activeStep === 0 ? "var(--bg)" : "white",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                marginRight: 1,
              }}
            >
              1
            </Typography>
            <Typography>Bulk Upload</Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: activeStep === 1 ? "var(--bg)" : "#E2E8F0",
              color: activeStep === 1 ? "white" : "var(--bg)",
              padding: "10px 20px",
              borderRadius: "0 4px 4px 0",
              position: "relative",
              "&:before": {
                content: '""',
                position: "absolute",
                left: -10,
                width: 20,
                height: "100%",
                backgroundColor: "inherit",
                clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                transform: "rotate(-90deg)",
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: activeStep === 1 ? "white" : "#A0AEC0",
                color: activeStep === 1 ? "#2D3748" : "#4A5568",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                marginRight: 1,
              }}
            >
              2
            </Typography>
            <Typography>Overview</Typography>
          </Box>
        </Box>

        {/* Content Area - with flex-grow to push buttons to bottom */}
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {activeStep === 0 ? (
            <Box sx={{ p: 2 }}>
              {/* Basic Details */}
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Basic Details
              </Typography>

              <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
                <Autocomplete
                  id="account-name-autocomplete"
                  options={accountNames || []}
                  value={
                    accountNames?.find(
                      (opt) => opt.accountName === localFormData?.accountName
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    handleInputChange(
                      "accountName",
                      newValue?.accountName || ""
                    );
                  }}
                  getOptionLabel={(option) => option.accountName || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Account Name"
                      placeholder="Select or search account"
                      variant="outlined"
                      error={!!errors.accountName}
                      helperText={errors.accountName}
                      required
                    />
                  )}
                  sx={{ flex: 1 }}
                  readOnly={accountNames && accountNames.length === 1}
                  disableClearable
                />

                <Autocomplete
                  id="category-autocomplete"
                  options={category}
                  value={selectedCategory}
                  onChange={(e, newValue) => {
                    console.log("Selected category:", newValue);
                    setSelectedCategory(newValue);
                    if (newValue) {
                      handleInputChange("category", newValue.value);
                    } else {
                      handleInputChange("category", "");
                    }

                    // Reset action type when category changes
                    setSelectedActionType(null);
                    handleInputChange("actionType", "");
                    handleInputChange("uploadedFile", null);
                    handleInputChange("fileData", null);
                    handleInputChange("processedData", null);

                    // 🔹 Also reset local states so UI messages clear
                    setLocalFormData((prev) => ({
                      ...prev,
                      uploadedFile: null,
                      fileData: null,
                      processedData: null,
                    }));
                    setFileName("");
                    setErrors({});
                  }}
                  getOptionLabel={(option) => option.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      placeholder="Select or search category"
                      variant="outlined"
                      error={!!errors.category}
                      helperText={errors.category}
                      required
                    />
                  )}
                  sx={{ flex: 1 }}
                  disableClearable
                />

                {selectedCategory?.value === "Update SIM Status" && (
                  <Autocomplete
                    id="action-type-autocomplete"
                    options={actionTypeOptions[selectedCategory.value] || []}
                    value={selectedActionType}
                    onChange={(e, newValue) => {
                      console.log("Selected action type:", newValue);
                      setSelectedActionType(newValue);
                      if (newValue) {
                        handleInputChange("actionType", newValue.value);
                      } else {
                        handleInputChange("actionType", "");
                      }
                    }}
                    getOptionLabel={(option) => option.label || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Action Type"
                        placeholder="Select action type"
                        variant="outlined"
                        required
                        error={!!errors.actionType}
                        helperText={errors.actionType}
                      />
                    )}
                    disabled={!selectedCategory}
                    sx={{ flex: 1 }}
                    disableClearable
                  />
                )}

                {selectedActionType?.value === "Activate SIM" && (
                 // Replace the existing plan Autocomplete component with this updated version:
<Autocomplete
  id="plan-type-autocomplete"
  options={plans}
  value={selectedPlan}
  onChange={(e, newValue) => {
    console.log("Selected plan:", newValue);
    setSelectedPlan(newValue);
    
    // Update both planType and planId in localFormData
    const updatedFormData = {
      ...localFormData,
      planType: newValue?.planName || '',
      planId: newValue?.id || newValue?.planId || '' // Handle both id and planId properties
    };
    
    setLocalFormData(updatedFormData);
    
    // Also update through the handler
    if (typeof onFormDataChange === "function") {
      onFormDataChange(updatedFormData);
    }
  }}
  getOptionLabel={(option) => option.planName || ""}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Select Plan"
      placeholder="Select Plan Type"
      variant="outlined"
      required
      error={!!errors.planType}
      helperText={errors.planType}
    />
  )}
  disabled={!selectedActionType}
  sx={{ flex: 1 }}
  disableClearable
/>
                )}
               <Box sx={{ mt: 1 }}>
  <FormControl>
    <FormControlLabel
      control={
        <Checkbox
          checked={localFormData.airtel || false}
          onChange={(e) => {
            handleInputChange("airtel", e.target.checked);
          }}
          color="primary"
        />
      }
      label="Airtel Profile"
    />
    {!localFormData.airtel && (
      <FormHelperText error>
        Please check this box to proceed if SIM type is Airtel.
      </FormHelperText>
    )}
  </FormControl>
</Box>

                
              </Box>

              {/* Instructions */}
              {currentInstructions && (
                <Paper
                  elevation={0}
                  sx={{
                    mb: 4,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setInstructionsExpanded(!instructionsExpanded)
                    }
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {currentInstructions.title}
                    </Typography>
                    <IconButton size="small">
                      {instructionsExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={instructionsExpanded}>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <List dense>
                        {currentInstructions.items.map((item, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={`• ${item}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { lineHeight: 1.5 },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Collapse>
                </Paper>
              )}

              {/* Bulk Upload */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Bulk Upload
                </Typography>
                <a
                  href={
                    selectedCategory && sampleFiles[selectedCategory.value]
                      ? sampleFiles[selectedCategory.value]
                      : "#"
                  }
                  download={
                    selectedCategory
                      ? `${selectedCategory.value
                          .toLowerCase()
                          .replace(/\s+/g, "_")}_template.xlsx`
                      : "sample_file.xlsx"
                  }
                >
                  <Button
                    variant="text"
                    size="small"
                    sx={{ textDecoration: "underline", textTransform: "none" }}
                    disabled={!selectedCategory}
                  >
                    download{" "}
                    {selectedCategory
                      ? `${selectedCategory.value
                          .toLowerCase()
                          .replace(/\s+/g, "_")}_template.xlsx`
                      : "sample file"}
                  </Button>
                </a>
              </Box>

              {/* File Upload Area */}
              <Paper
                elevation={0}
                sx={{
                  border: dragActive
                    ? "2px dashed #1976d2"
                    : "2px dashed #e0e0e0",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  backgroundColor: dragActive ? "#f3f8ff" : "#fafafa",
                  cursor: "pointer",
                  mb: 2,
                  transition: "all 0.3s ease",
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                <DescriptionIcon
                  sx={{ fontSize: 48, color: "#4caf50", mb: 2 }}
                />

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                  Upload
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  <strong>Drag</strong> and <strong>Drop</strong> your file here
                  or <strong>Browse</strong> to upload
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Supported Files and Sizes are <strong>CSV and Excel</strong>{" "}
                  maximum number of records is 1,000 with max size of{" "}
                  <strong>10 MB</strong>
                </Typography>

                {localFormData?.uploadedFile && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "#e8f5e8",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="success.main">
                      ✓ {localFormData.uploadedFile.name} uploaded successfully
                    </Typography>
                  </Box>
                )}
              </Paper>

              {errors.file && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.file}
                </Alert>
              )}

              <Typography
                variant="caption"
                color="error"
                sx={{ float: "right", mb: 2 }}
              >
                *Mandatory
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <TransactionOverview
                fileData={localFormData?.processedData || []}
                fileName={fileName}
                accountName={localFormData?.accountName}
                category={selectedCategory?.value || localFormData?.category}
                actionType={localFormData?.actionType}
                uploadedFile={localFormData?.uploadedFile}
                planType={localFormData?.planType}
                planId={localFormData?.planId}
                onPrevious={handlePrevious}
                onSubmit={(responseData) => {
                  // Handle successful submission
                  setLocalFormData({});
                  setActiveStep(0);
                  if (typeof onNext === "function") {
                    onNext(responseData);
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Action Buttons - Only shown on the first step */}
        {activeStep === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                px: 4,
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "#666",
              }}
            >
              cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!localFormData.airtel} // Disable if Airtel checkbox is not checked
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: localFormData.airtel ? "var(--bg)" : "#ccc",
                "&:hover": {
                  backgroundColor: localFormData.airtel ? "var(--bg)" : "#ccc",
                },
              }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}

export default TransactionExcelUpload;
